import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOperatorAction } from 'src/hooks/useTriggerOperatorAction';
import startCase from 'lodash/startCase';
import { OperatorModalContext } from 'src/context/operatorModalContext';
import {
  Box,
  Button,
  Modal,
  SpaceBetween,
  TextContent,
} from '@cloudscape-design/components';
import { PermissionAwareButton } from 'src/components/PermissionAwareButton';
import { ToastMessageContext } from 'src/context/toastMessageContext';

export const RetryModal = () => {
  const { triggerOperatorAction } = useOperatorAction();
  const { retryConfig, setRetryConfig } = useContext(OperatorModalContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const navigate = useNavigate();

  const { addToastMessage } = useContext(ToastMessageContext);

  if (!retryConfig) return <></>;
  const friendlyAction = startCase(retryConfig.action);

  const onClose = () => {
    setError(null);
    setRetryConfig(null);
  };

  return (
    <Modal
      header={`${friendlyAction} ${retryConfig.operatorName} Operator?`}
      visible={!!retryConfig}
      onDismiss={onClose}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onClose}>
              Cancel
            </Button>
            <PermissionAwareButton
              requiredPermission="READ_AND_LIMITED_ACTION"
              actionDescription="retry operators"
              loading={loading}
              variant="primary"
              onClick={() => {
                setLoading(true);
                setError(null);
                triggerOperatorAction(retryConfig)
                  .then(() => {
                    addToastMessage({
                      header: `Processing Request`,
                      description: `The ${friendlyAction} request for ${retryConfig.operatorName} is being processed. This page will refresh in one minute.`,
                      type: 'success',
                      dismissTime: 60000,
                      /* istanbul ignore next */
                      onDismiss: () => {
                        /* istanbul ignore next */
                        navigate(0);
                      },
                    });
                    onClose();
                  })
                  .catch(setError)
                  .finally(() => {
                    setLoading(false);
                  });
              }}
            >
              {friendlyAction}
            </PermissionAwareButton>
          </SpaceBetween>
        </Box>
      }
    >
      {retryConfig.action === 'retry' && (
        <TextContent>
          <p>
            If you updated the user script, please trigger a new operator flow
            job. Operator node retry button will <strong>NOT</strong> run with
            the updated user script, instead, it is using the user script
            snapshot that was created when this current operator flow job was
            triggered.
          </p>
          <p>
            Before you retry the failed operator node, please check the log of
            the failed operator node by clicking “View Logs” button and search
            for “error” or “exception” keywords for further investigation.
          </p>
          <p>
            If you see error messages similar to “AlgorithmError: See job logs
            for more information”, you will need to check the Cloudwatch log of
            the corresponding SageMaker job in your own AWS account for log
            information.
          </p>
          <p>
            After retrying the operator node, please allow 1-2 minutes before
            the updated status show up on MLPigeon portal.
          </p>
          {error && <p className="error-text">{error.message}</p>}
        </TextContent>
      )}
    </Modal>
  );
};
